<template>
  <div class="story-page" :class="{ 'story-page--mobile': !isPC }">
    <div class="desktop-header">
      <div class="header-left">
        <h1 class="page-title">我的故事</h1>
        <span v-if="totalCount > 0" class="story-total">{{ totalCount }}个故事</span>
      </div>

      <div class="header-actions">
        <div class="search-field">
          <input
            ref="desktopSearchInputRef"
            v-model="searchKeyword"
            class="search-input"
            type="search"
            placeholder="搜索作品"
            aria-label="搜索作品"
            @keydown.esc="clearSearch(desktopSearchInputRef)"
          />
          <button
            v-if="searchKeyword"
            class="search-clear-button"
            type="button"
            aria-label="清除搜索"
            @click="clearSearch(desktopSearchInputRef)"
          >
            <img
              class="search-clear-icon"
              :src="storySearchClearIconUrl"
              alt=""
              aria-hidden="true"
            />
          </button>
          <SvgIcon v-else name="story-search-desktop" :size="20" />
        </div>

        <el-button
          class="create-button"
          type="primary"
          :loading="isCheckingCreation"
          @click="handleCreate"
        >
          <SvgIcon name="magic-wand" :size="16" />
          <span>创建绘本</span>
        </el-button>
      </div>
    </div>

    <div class="mobile-header-stack">
      <div v-if="!isMobileSearchVisible" class="mobile-title-row">
        <div class="mobile-title-copy">
          <h1 class="page-title">我的故事</h1>
          <span v-if="totalCount > 0" class="story-total">{{ totalCount }}个故事</span>
        </div>
        <button
          class="mobile-search-button"
          type="button"
          aria-label="搜索故事"
          @click="openMobileSearch"
        >
          <SvgIcon name="story-search-open" :size="24" />
        </button>
      </div>

      <div v-else class="mobile-search-row">
        <div class="mobile-search-field">
          <SvgIcon name="story-search" :size="12" />
          <input
            ref="mobileSearchInputRef"
            v-model="searchKeyword"
            class="mobile-search-input"
            type="search"
            placeholder="搜索故事"
            aria-label="搜索故事"
          />
          <button
            v-if="showSearchEmptyState"
            class="search-clear-button mobile-search-clear-button"
            type="button"
            aria-label="清除搜索"
            @click="clearSearch(mobileSearchInputRef)"
          >
            <img
              class="search-clear-icon"
              :src="storySearchClearIconUrl"
              alt=""
              aria-hidden="true"
            />
          </button>
        </div>
        <button class="mobile-search-cancel" type="button" @click="closeMobileSearch">取消</button>
      </div>
    </div>

    <div ref="contentAreaRef" class="content-area">
      <div v-if="showEmptyState" class="story-empty" role="status">
        <div class="story-empty__content">
          <div class="story-empty__intro">
            <img
              class="story-empty__illustration"
              :src="storyEmptyIllustrationUrl"
              alt=""
              aria-hidden="true"
            />
            <p class="story-empty__title">还没有绘本故事</p>
          </div>
          <el-button
            class="story-empty__button"
            type="primary"
            :loading="isCheckingCreation"
            @click="handleCreate"
          >
            <SvgIcon name="story-empty-create" :size="18" />
            <span>立即创作第一本绘本</span>
          </el-button>
        </div>
      </div>

      <div v-else-if="showSearchEmptyState" class="story-search-empty" role="status">
        <img
          class="story-search-empty__illustration"
          :src="storySearchEmptyIllustrationUrl"
          alt=""
          aria-hidden="true"
        />
        <p class="story-search-empty__text">无搜索结果</p>
      </div>

      <ScrollBar v-else :item-gap="19" :scrollbar-offset="0" @end-reached="handleLoadMore">
        <StoryListItem
          v-for="story in visibleStories"
          :key="story.id"
          :story="story"
          :thumbnail-timestamp="thumbnailTimestamp"
          :is-free-user="userStore.isLogin && userStore.isFreeUser"
          :compact="isCompactStoryItem"
          :is-sharing="isStorySharing(story.id)"
          @read="handleRead"
          @edit="handleEdit"
          @share="handleShare"
          @export-pdf="handleExportPdf"
          @delete="handleDelete"
          @title-updated="handleTitleUpdated"
          @upgrade="showUpgrade"
        />
      </ScrollBar>
    </div>

    <el-button
      class="mobile-create-button"
      type="primary"
      :loading="isCheckingCreation"
      @click="handleCreate"
    >
      <SvgIcon name="magic-wand" :size="16" />
      <span>创建绘本</span>
    </el-button>

    <BookSharePanel
      v-if="isPC && !isCompactStoryItem"
      :visible="shareVisible"
      :title="shareTitle"
      :anchor-rect="shareAnchorRect"
      @close="closeSharePanel"
    />
    <BookSharePanelMobile
      v-else
      :visible="shareVisible"
      :title="shareTitle"
      @close="closeSharePanel"
    />

    <EditorPreviewModal :visible="previewVisible" @close="previewVisible = false" />

    <StoryStorageLimitDialog
      v-model="isStorageLimitVisible"
      :membership-level="membershipLevel"
      @manage="handleManageStories"
      @upgrade="showUpgrade"
    />
  </div>
</template>

<script setup lang="ts">
  import { computed, nextTick, onActivated, onBeforeUnmount, onMounted, ref, watch } from 'vue';
  import { useRouter } from 'vue-router';
  import { ElMessage } from 'element-plus';
  import type { ScrollbarDirection } from 'element-plus';
  import { deleteStory, fetchStoryList, STORY_PAGE_SIZE } from '@/api/story';
  import SvgIcon from '@/components/common/icon/SvgIcon.vue';
  import StoryStorageLimitDialog from '@/components/common/StoryStorageLimitDialog.vue';
  import ScrollBar from '@/components/common/list/ScrollBar.vue';
  import StoryListItem from '@/components/common/list/item/StoryListItem.vue';
  import BookSharePanel from '@/views/book/components/BookSharePanel.vue';
  import BookSharePanelMobile from '@/views/book/components/BookSharePanelMobile.vue';
  import { useViewportAccessGate } from '@/hooks/useViewportAccessGate';
  import { useUpgradeModal } from '@/hooks/useUpgradeModal';
  import { useProjectStore, useUserStore } from '@/store';
  import { emitter } from '@/utils';
  import { EVENT_NAMES } from '@/constants';
  import type { StoryItem } from '@/types';
  import { prepareBookForRead } from '@/modules/editor/canvas/models/template/kieStoryParser';
  import { projectManager } from '@/managers';
  import EditorPreviewModal from '@/views/editor/components/EditorPreviewModal.vue';
  // import { createPageErrorEditWindowName } from '@/views/book/pageErrorEditAccess';
  import { getStoryStorageLimit, normalizeStoryMembershipLevel } from '@/utils/storyStorage';
  import storyEmptyIllustrationUrl from '@/assets/icons/svg/story-empty.svg?url';
  import storySearchEmptyIllustrationUrl from '@/assets/icons/svg/story-search-empty.svg?url';
  import storySearchClearIconUrl from '@/assets/icons/svg/story-search-clear.svg?url';

  const router = useRouter();
  const projectStore = useProjectStore();
  const userStore = useUserStore();
  const { isPC } = useViewportAccessGate();
  const { showUpgrade } = useUpgradeModal();

  // ==================== 数据 ====================
  const stories = ref<StoryItem[]>([]);
  const searchKeyword = ref('');
  const isMobileSearchVisible = ref(false);
  const desktopSearchInputRef = ref<HTMLInputElement>();
  const mobileSearchInputRef = ref<HTMLInputElement>();
  const contentAreaRef = ref<HTMLElement>();
  const contentAreaWidth = ref(Number.POSITIVE_INFINITY);
  const currentPage = ref(1);
  const totalCount = ref(0);
  const isLoading = ref(false);
  const loadedKeyword = ref<string | null>(null);
  const thumbnailTimestamp = ref(Date.now());
  const isCheckingCreation = ref(false);
  const isStorageLimitVisible = ref(false);
  /** 分享面板显隐 */
  const shareVisible = ref(false);
  /** 预览弹窗（转存未完成时） */
  const previewVisible = ref(false);
  /** 当前分享中的故事标题 */
  const shareTitle = ref('');
  /** PC 端面板相对「分享」按钮的定位锚点 */
  const shareAnchorRect = ref<DOMRect | null>(null);
  /** 正在准备分享的故事 id（按书维度，用于按钮 loading / 同书防连点） */
  const sharingStoryIds = ref(new Set<string>());
  const deletingStoryIds = new Set<string>();
  let searchTimer: number | undefined;
  let latestRequestId = 0;

  /** 分享准备请求序号：仅最新一次解析完成后才写入面板状态 */
  let latestShareRequestId = 0;

  /** 判断指定故事是否正在准备分享 */
  const isStorySharing = (storyId: string) => sharingStoryIds.value.has(storyId);

  /** 更新某本书的分享 loading 状态（拷贝 Set 以触发视图更新） */
  const setStorySharing = (storyId: string, sharing: boolean) => {
    const next = new Set(sharingStoryIds.value);
    if (sharing) next.add(storyId);
    else next.delete(storyId);
    sharingStoryIds.value = next;
  };

  let contentAreaResizeObserver: ResizeObserver | undefined;

  const STORY_COMPACT_WIDTH = 960;

  const hasMore = computed(() => stories.value.length < totalCount.value);
  const visibleStories = computed(() => stories.value);
  const isCompactStoryItem = computed(
    () => !isPC.value || contentAreaWidth.value < STORY_COMPACT_WIDTH
  );
  const showEmptyState = computed(
    () =>
      !isLoading.value &&
      !searchKeyword.value.trim() &&
      loadedKeyword.value === '' &&
      stories.value.length === 0
  );
  const showSearchEmptyState = computed(() => {
    const keyword = searchKeyword.value.trim();

    return !!keyword && !!loadedKeyword.value && stories.value.length === 0;
  });
  const membershipLevel = computed(() => {
    return normalizeStoryMembershipLevel(
      userStore.userData.aipicturebook_vip_type ?? userStore.userData.uType ?? 0
    );
  });

  onActivated(async () => {
    await loadStories({ reset: true });
  });

  onMounted(() => {
    const contentArea = contentAreaRef.value;
    if (!contentArea) return;

    contentAreaWidth.value = contentArea.getBoundingClientRect().width;
    contentAreaResizeObserver = new ResizeObserver(([entry]) => {
      if (entry) contentAreaWidth.value = entry.contentRect.width;
    });
    contentAreaResizeObserver.observe(contentArea);
  });

  onBeforeUnmount(() => {
    if (searchTimer) window.clearTimeout(searchTimer);
    contentAreaResizeObserver?.disconnect();
  });

  watch(searchKeyword, () => {
    if (searchTimer) window.clearTimeout(searchTimer);

    searchTimer = window.setTimeout(() => {
      void loadStories({ reset: true });
    }, 300);
  });

  // ==================== 操作 ====================
  const handleCreate = async () => {
    if (isCheckingCreation.value) return;

    const storyLimit = getStoryStorageLimit(membershipLevel.value);
    if (!Number.isFinite(storyLimit)) {
      await router.push({ name: 'create' });
      return;
    }

    if (loadedKeyword.value === '') {
      if (totalCount.value >= storyLimit) {
        isStorageLimitVisible.value = true;
        return;
      }

      await router.push({ name: 'create' });
      return;
    }

    isCheckingCreation.value = true;

    try {
      const result = await fetchStoryList({ current: 1, size: 1 });
      if (result.total >= storyLimit) {
        isStorageLimitVisible.value = true;
        return;
      }

      await router.push({ name: 'create' });
    } catch (error) {
      console.error('检查绘本存储额度失败', error);
      ElMessage.error(getErrorMessage(error, '检查绘本存储额度失败，请稍后重试'));
    } finally {
      isCheckingCreation.value = false;
    }
  };

  const handleManageStories = () => {
    isStorageLimitVisible.value = false;
    void router.push({ name: 'story' });
  };

  const openMobileSearch = async () => {
    isMobileSearchVisible.value = true;
    await nextTick();
    mobileSearchInputRef.value?.focus();
  };

  const closeMobileSearch = () => {
    isMobileSearchVisible.value = false;
    searchKeyword.value = '';
  };

  const clearSearch = async (input?: HTMLInputElement) => {
    searchKeyword.value = '';
    await nextTick();
    input?.focus();
  };

  // const openReaderWindow = (url: string, fromReadButton: boolean) => {
  //   if (!fromReadButton) {
  //     window.open(url, '_blank', 'noopener');
  //     return;
  //   }

  //   const readerWindow = window.open(url, createPageErrorEditWindowName());
  //   if (readerWindow) {
  //     readerWindow.opener = null;
  //   }
  // };

  const handleRead = async (story: StoryItem, _fromReadButton = false) => {
    try {
      // 步骤 1：解析并尝试写回，按转存状态分流
      const result = await prepareBookForRead(story.id as string);

      await projectManager.loadPreview(result.data);
      previewVisible.value = true;

      // 步骤 2：未转存完 → 预览弹窗
      // if (result.mode === 'preview') {
      //   await projectManager.loadPreview(result.data);
      //   previewVisible.value = true;
      //   return;
      // }

      // // 步骤 3：已转存 → 打开发布目录
      // const shareUrl = result.data.project?.shareUrl || story.url;
      // if (!shareUrl) {
      //   openReaderWindow(
      //     router.resolve({ name: 'book', params: { id: story.id } }).href,
      //     _fromReadButton
      //   );
      //   return;
      // }

      // const folder = shareUrl.replace(/[^/]+$/, '');
      // openReaderWindow(folder, _fromReadButton);
    } catch (error: unknown) {
      ElMessage.error(getErrorMessage(error, '打开阅读失败，请稍后重试'));
    }
  };

  const handleEdit = (story: StoryItem) => {
    router.push({ name: 'edit', params: { id: story.id } });
  };

  const handleTitleUpdated = (storyId: string, title: string) => {
    const story = stories.value.find((item) => item.id === storyId);
    if (story) story.title = title;
  };

  /**
   * 分享前先确保工程数据已解析并写回；否则访客打开分享链接会因缺少 project 而空白。
   * 面板状态只在解析完成后写入；并发点击不同书时仅最新一次生效；同书 loading 防连点。
   */
  const handleShare = async (story: StoryItem, event: MouseEvent) => {
    const storyId = story.id ? String(story.id) : '';
    // 步骤 1：无 id 或该书正在准备分享时直接返回（不挡其它书）
    if (!storyId || sharingStoryIds.value.has(storyId)) return;

    // 步骤 2：await 前先记下锚点，避免事件目标失效
    const target = event.currentTarget as HTMLElement | null;
    const anchorRect = target?.getBoundingClientRect() ?? null;

    const doShare = async () => {
      // 步骤 3：水印确认回调可能重复触发，再次按书维度拦截
      if (sharingStoryIds.value.has(storyId)) return;

      const requestId = ++latestShareRequestId;
      setStorySharing(storyId, true);

      try {
        // 步骤 4：仅分享点击路径解析并写回；已有 project 则快速返回
        const result = await prepareBookForRead(storyId);

        // 步骤 5：已被更新的分享请求取代时，丢弃本次结果
        if (requestId !== latestShareRequestId) return;

        // 步骤 6：转存未完成仍允许分享，但提示访客可能暂时无法阅读
        if (result.mode === 'preview') {
          ElMessage.warning('内容可能尚未完全就绪，分享后访客可能暂时无法正常阅读');
        }

        // 步骤 7：解析完成后才写入分享信息并打开面板
        shareTitle.value = story.title || '未命名绘本';
        projectStore.shareUrl = result.data.project?.shareUrl || resolveStoryShareUrl(story);
        projectStore.shareCoverUrl = story.coverUrl || '';
        shareAnchorRect.value = anchorRect;
        shareVisible.value = true;
      } catch (error: unknown) {
        if (requestId !== latestShareRequestId) return;
        ElMessage.error(getErrorMessage(error, '准备分享失败，请稍后重试'));
      } finally {
        setStorySharing(storyId, false);
      }
    };

    if (userStore.userMeta.first_export) {
      emitter.emit(EVENT_NAMES.WATERMARK_DIALOG_OPEN, { resolve: doShare });
      await userStore.setMeta({ first_export: false });
    } else {
      await doShare();
    }
  };

  /** 关闭分享面板并清理锚点 */
  const closeSharePanel = () => {
    shareVisible.value = false;
    shareAnchorRect.value = null;
  };

  /**
   * 解析故事可分享的阅读页链接。
   * 与 kieStoryParser 一致，分享地址应包含 index.html。
   */
  const resolveStoryShareUrl = (story: StoryItem) => {
    if (story.url) {
      const url = story.url.trim();

      // 步骤 1：已是完整阅读页地址时直接返回
      if (/index\.html(?:[?#]|$)/i.test(url)) {
        return url;
      }

      // 步骤 2：目录地址补全 index.html
      const folder = url.endsWith('/') ? url : `${url.replace(/\/[^/]*$/, '')}/`;
      return `${folder}index.html`;
    }

    return `${window.location.origin}${
      router.resolve({ name: 'book', params: { id: story.id } }).href
    }`;
  };

  const handleExportPdf = async (story: StoryItem) => {
    const navigateToEditor = () => {
      router.push({ name: 'edit', params: { id: story.id }, query: { exportPdf: 'true' } });
    };

    if (userStore.userMeta.first_export) {
      emitter.emit(EVENT_NAMES.WATERMARK_DIALOG_OPEN, { resolve: navigateToEditor });
      await userStore.setMeta({ first_export: false });
    } else {
      navigateToEditor();
    }
  };

  const handleDelete = async (story: StoryItem) => {
    if (deletingStoryIds.has(story.id)) return;

    deletingStoryIds.add(story.id);

    try {
      await deleteStory(story.id);

      const nextStories = stories.value.filter((s) => s.id !== story.id);
      if (nextStories.length !== stories.value.length) {
        stories.value = nextStories;
        totalCount.value = Math.max(0, totalCount.value - 1);
      }

      ElMessage.success('已删除');
    } catch (error) {
      console.error('删除故事失败', error);
      ElMessage.error(getErrorMessage(error, '删除故事失败，请稍后重试'));
    } finally {
      deletingStoryIds.delete(story.id);
    }
  };

  const handleLoadMore = (direction: ScrollbarDirection) => {
    if (direction !== 'bottom' || isLoading.value || !hasMore.value) return;

    void loadStories();
  };

  async function loadStories({ reset = false }: { reset?: boolean } = {}) {
    if (!reset && isLoading.value) return;

    const requestId = latestRequestId + 1;
    latestRequestId = requestId;
    isLoading.value = true;
    const keyword = searchKeyword.value.trim();

    try {
      const result = await fetchStoryList({
        current: reset ? 1 : currentPage.value + 1,
        size: STORY_PAGE_SIZE,
        keyword
      });

      if (requestId !== latestRequestId) return;

      thumbnailTimestamp.value = Date.now();
      stories.value = reset ? result.list : [...stories.value, ...result.list];
      currentPage.value = result.current;
      totalCount.value = result.total;
      loadedKeyword.value = keyword;
    } catch (error) {
      if (requestId !== latestRequestId) return;

      console.error('获取故事列表失败', error);
      ElMessage.error(getErrorMessage(error));
    } finally {
      if (requestId === latestRequestId) {
        isLoading.value = false;
      }
    }
  }

  function getErrorMessage(error: unknown, fallback = '获取故事列表失败，请稍后重试') {
    if (error instanceof Error && error.message) return error.message;

    if (typeof error === 'object' && error !== null) {
      const record = error as { msg?: unknown; errorMsg?: unknown };
      if (typeof record.msg === 'string' && record.msg) return record.msg;
      if (typeof record.errorMsg === 'string' && record.errorMsg) return record.errorMsg;
    }

    return fallback;
  }
</script>

<style scoped>
  .story-page {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: 24px;
    overflow: hidden;
  }

  /* ===== 页面头部 ===== */
  .desktop-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 40px;
    padding-right: 14px;
    backdrop-filter: blur(12px);
    flex-shrink: 0;
    z-index: 1;
  }

  .header-left {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
  }

  .page-title {
    margin: 0;
    color: #333;
    font-family: 'Source Han Sans CN VF', 'Noto Sans SC', 'Microsoft YaHei', sans-serif;
    font-size: 24px;
    font-weight: 700;
    line-height: 32px;
  }

  .story-total {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 36px;
    padding: 0 16px;
    box-sizing: border-box;
    border-radius: 16px;
    background: #ede8ff;
    color: #6033e1;
    font-family: 'Source Han Sans CN VF', 'Noto Sans SC', 'Microsoft YaHei', sans-serif;
    font-size: 13px;
    font-weight: 500;
    line-height: 19px;
    white-space: nowrap;
  }

  .header-actions {
    display: flex;
    align-items: center;
    gap: 31px;
    flex-shrink: 0;
  }

  .mobile-title-row {
    display: none;
  }

  .mobile-header-stack {
    display: none;
  }

  .mobile-search-button {
    display: none;
  }

  .mobile-search-row {
    display: none;
  }

  .search-field {
    display: inline-flex;
    align-items: center;
    justify-content: space-between;
    width: 282px;
    height: 36px;
    padding: 0 11px;
    box-sizing: border-box;
    border: 1px solid #eaeaea;
    border-radius: 6px;
    background: #fff;
    color: #333;
  }

  .search-field:hover,
  .search-field:focus-within {
    border-color: #6033e1;
  }

  .search-input {
    width: 100%;
    min-width: 0;
    border: 0;
    outline: 0;
    padding: 0;
    background: transparent;
    color: #160211;
    font-family: 'Inter', 'Microsoft YaHei', 'Noto Sans SC', sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 22px;
  }

  .search-input::placeholder {
    color: #999;
  }

  .search-input::-webkit-search-cancel-button {
    display: none;
  }

  .search-clear-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    flex: 0 0 16px;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background: transparent;
    color: #6e6a7c;
    cursor: pointer;
  }

  .search-clear-button:focus-visible {
    outline: 2px solid rgba(96, 51, 225, 0.35);
    outline-offset: 2px;
  }

  .search-clear-icon {
    display: block;
    width: 16px;
    height: 16px;
  }

  /* ===== 创建绘本按钮 ===== */
  .create-button {
    width: 120px;
    height: 40px;
    padding: 0;
    border: 0;
    border-radius: 12px;
    background: #6033e1;
    color: #fff;
    font-size: 14px;
    font-weight: 500;
    box-shadow:
      0 10px 15px -3px rgba(99, 102, 241, 0.2),
      0 4px 6px -4px rgba(99, 102, 241, 0.2);
  }

  .create-button :deep(span) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
  }

  .create-button:hover,
  .create-button:focus {
    background: #6033e1;
    color: #fff;
  }

  .mobile-create-button {
    display: none;
  }

  /* ===== 内容区域 ===== */
  .content-area {
    flex: 1;
    min-height: 0;
    overflow: hidden;
  }

  .story-empty {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
  }

  .story-empty__content {
    width: 217px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 28px;
  }

  .story-empty__intro {
    width: 200px;
    height: 208px;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .story-empty__illustration {
    width: 200px;
    height: 180px;
  }

  .story-empty__title {
    width: 200px;
    height: 28px;
    margin: 0;
    color: #160211;
    font-family: 'Noto Sans SC', 'Source Han Sans CN VF', 'Microsoft YaHei', sans-serif;
    font-size: 20px;
    font-weight: 400;
    line-height: 28px;
    text-align: center;
  }

  .story-empty__button {
    width: 217px;
    height: 48px;
    padding: 0 28px;
    border: 0;
    border-radius: 16px;
    background: #6033e1;
    color: #fff;
    font-family: 'Noto Sans SC', 'Source Han Sans CN VF', 'Microsoft YaHei', sans-serif;
    font-size: 15px;
    font-weight: 600;
    line-height: 22.5px;
    box-shadow: none;
  }

  .story-empty__button :deep(span) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    white-space: nowrap;
  }

  .story-empty__button:hover,
  .story-empty__button:focus {
    background: #6033e1;
    color: #fff;
  }

  .story-search-empty {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
  }

  .story-search-empty__illustration {
    width: 72px;
    height: 70px;
  }

  .story-search-empty__text {
    width: 72px;
    height: 16px;
    margin: 0;
    color: #6e6a7c;
    font-family: 'PingFang SC', 'Source Han Sans CN VF', 'Noto Sans SC', sans-serif;
    font-size: 13px;
    font-weight: 400;
    line-height: 16px;
    text-align: center;
  }

  /* ===== 移动端适配 ===== */
  .story-page.story-page--mobile {
    background: #fbf9ff;
    position: relative;
    gap: 16px;
  }

  .story-page--mobile .mobile-header-stack {
    display: flex;
    flex-direction: column;
    gap: 32px;
    padding: 16px 16px 0;
    box-sizing: border-box;
    flex-shrink: 0;
  }

  .story-page--mobile .desktop-header {
    display: none;
  }

  .story-page--mobile .page-title {
    font-family: 'PingFang SC', 'Source Han Sans CN VF', 'Noto Sans SC', sans-serif;
    color: #160211;
    font-size: 14px;
    line-height: 14px;
  }

  .story-page--mobile .mobile-title-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 36px;
    margin: 0;
    padding-left: 1px;
  }

  .story-page--mobile .mobile-title-copy {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
  }

  .story-page--mobile .story-total {
    height: 28px;
    padding: 0 12px;
    border-radius: 14px;
    font-size: 12px;
    line-height: 18px;
  }

  .story-page--mobile .mobile-search-row {
    display: flex;
    align-items: center;
    width: 100%;
    height: 36px;
    gap: 12px;
    margin: 0 0 0 -1px;
    flex-shrink: 0;
  }

  .story-page--mobile .mobile-search-field {
    display: inline-flex;
    align-items: center;
    width: calc(100% - 38px);
    height: 36px;
    min-width: 0;
    padding: 0 12px;
    box-sizing: border-box;
    border: 0;
    border-radius: 66px;
    background: #eff0f7;
    color: #160211;
    gap: 4px;
  }

  .story-page--mobile .mobile-search-input {
    width: auto;
    flex: 1;
    min-width: 0;
    height: 16px;
    padding: 0;
    border: 0;
    outline: 0;
    background: transparent;
    color: #160211;
    font-family: 'Source Han Sans CN VF', 'Noto Sans SC', 'Microsoft YaHei', sans-serif;
    font-size: 13px;
    font-weight: 400;
    line-height: 16px;
  }

  .story-page--mobile .mobile-search-input::placeholder {
    color: #6e6a7c;
    opacity: 1;
  }

  .story-page--mobile .mobile-search-input::-webkit-search-cancel-button {
    display: none;
  }

  .story-page--mobile .mobile-search-clear-button {
    margin-right: -2px;
  }

  .story-page--mobile .mobile-search-cancel {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 20px;
    padding: 0;
    border: 0;
    background: transparent;
    color: #6033e1;
    font-family: 'Arimo', 'PingFang SC', 'Microsoft YaHei', sans-serif;
    font-size: 13px;
    font-weight: 500;
    line-height: 20px;
    white-space: nowrap;
    cursor: pointer;
  }

  .story-page--mobile .mobile-search-cancel:focus-visible {
    outline: 2px solid rgba(96, 51, 225, 0.35);
    outline-offset: 2px;
  }

  .story-page--mobile .mobile-search-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    padding: 0;
    border: 0;
    border-radius: 16px;
    background: transparent;
    color: #160211;
    cursor: pointer;
  }

  .story-page--mobile .mobile-search-button :deep(.svg-icon) {
    width: 24px;
    height: 24px;
  }

  .story-page--mobile .mobile-search-button:hover,
  .story-page--mobile .mobile-search-button:focus {
    background: transparent;
    color: #160211;
  }

  .story-page--mobile .mobile-search-button:focus-visible {
    outline: 2px solid rgba(96, 51, 225, 0.35);
    outline-offset: 2px;
  }

  .story-page--mobile .create-button {
    display: none;
  }

  .story-page--mobile .mobile-create-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: calc(100% - 32px);
    height: 42px;
    margin: 0 16px 16px;
    padding: 0;
    border: 0;
    border-radius: 16px;
    background: #6033e1;
    color: #ffffff;
    font-family: 'PingFang SC', 'Microsoft YaHei', sans-serif;
    font-size: 13px;
    font-weight: 400;
    line-height: 18px;
    box-shadow: none;
  }

  .story-page--mobile .mobile-create-button :deep(span) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
  }

  .story-page--mobile .mobile-create-button:hover,
  .story-page--mobile .mobile-create-button:focus {
    background: #6033e1;
    color: #ffffff;
  }

  .story-page--mobile .content-area {
    min-height: 0;
    padding: 0 16px;
  }

  .story-page--mobile .story-empty {
    justify-content: center;
    padding-top: 0;
  }

  .story-page--mobile .story-empty__content {
    width: 195px;
    gap: 22px;
  }

  .story-page--mobile .story-empty__intro {
    width: 180px;
    height: 187px;
  }

  .story-page--mobile .story-empty__illustration {
    width: 180px;
    height: 162px;
  }

  .story-page--mobile .story-empty__title {
    width: 180px;
    height: 25px;
    font-size: 18px;
    line-height: 25px;
  }

  .story-page--mobile .story-empty__button {
    width: 195px;
    height: 44px;
    padding: 0 18px;
    font-size: 14px;
  }

  .story-page--mobile .content-area :deep(.list-container) {
    flex-direction: column;
    flex-wrap: nowrap;
    gap: 12px !important;
    padding-bottom: 0;
  }
</style>
